import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useCart } from "@/hooks/useCart";

const CartPage: React.FC = () => {
  const { items, removeFromCart, updateQuantity, subtotal } = useCart();
  const shipping = subtotal >= 200 ? 0 : 15.99;
  const tax = subtotal * 0.08;
  const total = subtotal + shipping + tax;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h1 className="text-4xl font-black text-gray-900 mb-8">Shopping Cart</h1>
          {items.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-shopping-cart-line text-6xl text-gray-300 block mb-4"></i>
              <p className="text-xl text-gray-500 mb-6">Your cart is empty</p>
              <Link to="/products" className="inline-flex items-center gap-2 px-8 py-3 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">
                Browse Products <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-4">
                {items.map((item) => (
                  <div key={item.productId} className="flex gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-white">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link to={`/product/${item.productId}`} className="font-semibold text-gray-800 text-sm hover:text-orange-500 transition-colors line-clamp-2">{item.name}</Link>
                      <p className="text-orange-500 font-bold mt-1">${item.price}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <button onClick={() => removeFromCart(item.productId)} className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer">
                        <i className="ri-delete-bin-line"></i>
                      </button>
                      <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden bg-white">
                        <button onClick={() => updateQuantity(item.productId, item.quantity - 1)} className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 cursor-pointer">
                          <i className="ri-subtract-line text-xs"></i>
                        </button>
                        <span className="w-8 text-center text-sm font-semibold">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.productId, item.quantity + 1)} className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 cursor-pointer">
                          <i className="ri-add-line text-xs"></i>
                        </button>
                      </div>
                      <p className="text-sm font-bold text-gray-800">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="lg:col-span-1">
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 sticky top-28">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Order Summary</h3>
                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Subtotal</span><span>${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Shipping</span>
                      <span>{shipping === 0 ? <span className="text-green-600">FREE</span> : `$${shipping.toFixed(2)}`}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Tax (8%)</span><span>${tax.toFixed(2)}</span>
                    </div>
                    {subtotal < 200 && (
                      <p className="text-xs text-orange-500 bg-orange-50 p-2 rounded-lg">
                        Add ${(200 - subtotal).toFixed(2)} more for free shipping!
                      </p>
                    )}
                  </div>
                  <div className="border-t border-gray-200 pt-4 mb-6">
                    <div className="flex justify-between font-bold text-gray-900">
                      <span>Total</span><span className="text-xl text-orange-500">${total.toFixed(2)}</span>
                    </div>
                  </div>
                  <Link
                    to="/checkout"
                    className="block w-full py-3.5 text-center bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap mb-3"
                  >
                    <i className="ri-lock-line mr-2"></i>Proceed to Checkout
                  </Link>
                  <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                    <i className="ri-shield-check-line text-green-500"></i>
                    <span>Secured by Stripe</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CartPage;
